import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {observer} from 'mobx-react';

import Button from '../components/ui-controls/Button';
import LastMinuteOptionTileCard from '../screens/cart/components/LastMinuteOptionTileCard';
import apiService from '../transport/apiService';
import {OrderLine} from '../transport/apiServiceResponse';

type CatalogueAddToCartModalProps = {
  productIds: string[];
  onClose: () => void;
};

const CatalogueAddToCartModal = observer(
  ({productIds, onClose}: CatalogueAddToCartModalProps) => {
    const [products, setProducts] = useState<OrderLine[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchProducts = async () => {
        try {
          const fetchedProducts = await Promise.all(
            productIds.map(id => apiService.getProductById(id)),
          );
          const foundProducts = fetchedProducts.filter(Boolean);

          if (foundProducts.length) {
            setProducts(foundProducts);
            setError(null);
          } else {
            setError("These products are not in stock in this store right now.");
            setProducts([]);
          }
        } catch (err) {
          setError(
            'Something went wrong finding your products.',
          );
          setProducts([]);
        } finally {
          setLoading(false);
        }
      };

      fetchProducts();
    }, [productIds]);

    if (loading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator />
        </View>
      );
    }

    if (error) {
      return (
        <View style={styles.container}>
          <View style={styles.loadingContainer}>
            <Text>{error}</Text>
            <Text>Please try again later.</Text>
          </View>
          <View style={styles.button}>
            <Button title="Close" onPress={onClose} />
          </View>
        </View>
      );
    }

    return (
      <View style={styles.container}>
        <FlatList
          data={products}
          renderItem={({item}) => (
            <LastMinuteOptionTileCard item={item} disablePress hasShadow containerStyles={({
              marginHorizontal: 8,
              marginBottom: 16,
            })} />
          )}
          keyExtractor={item => `${item.id}-0`}
          horizontal
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          style={styles.flatList}
          contentContainerStyle={styles.flatListContent}
        />
        <View style={styles.button}>
          <Button title="Close" onPress={onClose} />
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    justifyContent: 'space-between',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  flatList: {
    flex: 1,
  },
  flatListContent: {
    alignItems: 'center',
  },
  button: {
    flexDirection: 'column',
    justifyContent: 'flex-end',
    marginBottom: 16,
    paddingVertical: 16,
  },
});

export default CatalogueAddToCartModal;
